import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FOCUS_VISIBLE_RING_CLASS } from 'newApp/constants/accessibility';
import useModalBehavior from 'newApp/hooks/useModalBehavior';
import ConfirmationPortal from './portal/ConfirmationPortal';
import type { ConfirmationDialogState, ConfirmationVariant } from './types';
import SVGSpriteIcon from '../svg-sprite-icon/SVGSpriteIcon';

interface ConfirmationProps {
  dialog: ConfirmationDialogState | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const BUTTON_VARIANT_STYLES: Record<ConfirmationVariant, string> = {
  default:
    'tw-bg-primary tw-border-primary hover:tw-bg-primary hover:tw-text-white',
  danger: 'tw-bg-[#D4001B] tw-border-[#D4001B] hover:tw-bg-[#D4001B]',
  warning:
    'tw-bg-primary tw-border-primary hover:tw-bg-primary hover:tw-text-white',
  success:
    'tw-bg-primary tw-border-primary hover:tw-bg-primary hover:tw-text-white',
};

const Confirmation = ({ dialog, onConfirm, onCancel }: ConfirmationProps) => {
  const intl = useIntl();

  useModalBehavior(dialog?.closeOnEscape ? onCancel : undefined, Boolean(dialog));

  if (!dialog) {
    return null;
  }

  const titleId = `confirmation-title-${dialog.id}`;
  const messageId = `confirmation-message-${dialog.id}`;
  const isWarning = dialog.variant === 'warning';

  if (isWarning) {
    return (
      <ConfirmationPortal>
        <div
          className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-[rgba(17,24,39,0.58)] tw-p-4 sm:tw-p-6"
          onClick={() => {
            if (dialog.closeOnBackdropClick) {
              onCancel();
            }
          }}
        >
          <div
            aria-busy={dialog.isLoading}
            aria-describedby={messageId}
            aria-labelledby={titleId}
            aria-modal="true"
            className="tw-relative tw-w-[416px] tw-max-w-full tw-overflow-hidden tw-rounded tw-bg-white tw-px-9 tw-pb-9 tw-pt-[58px]"
            onClick={(event) => event.stopPropagation()}
            role="alertdialog"
          >
            <div className="tw-flex tw-flex-col tw-items-center">
              <div className="tw-mb-10 tw-flex tw-h-[72px] tw-w-[72px] tw-items-center tw-justify-center tw-rounded-full tw-border-[3px] tw-border-[#ff9900] tw-text-[42px] tw-font-light tw-leading-none tw-text-[#ff9900]">
                !
              </div>

              <h2
                className="tw-mb-9 tw-text-center tw-text-[15px] tw-font-bold tw-leading-7"
                id={titleId}
              >
                {dialog.title || dialog.message}
              </h2>

              {dialog.title && dialog.message && (
                <p className="tw-mb-9 tw-text-center tw-text-sm" id={messageId}>
                  {dialog.message}
                </p>
              )}

              <div className="tw-flex tw-items-center tw-justify-center tw-gap-3">
                <button
                  className={clsx(
                    'tw-h-11 tw-min-w-[48px] tw-rounded-md tw-border tw-border-primary tw-bg-primary tw-px-5 tw-py-2.5 tw-text-xs tw-font-bold tw-text-white hover:tw-bg-primary hover:tw-text-white',
                    FOCUS_VISIBLE_RING_CLASS,
                  )}
                  onClick={onConfirm}
                  type="button"
                >
                  {dialog.isLoading ? intl.formatMessage({ id: 'loading' }) : dialog.confirmText}
                </button>
                <button
                  className={clsx(
                    'tw-h-11 tw-min-w-[48px] tw-rounded-md tw-border tw-border-transparent tw-bg-[#fff5f7] tw-px-5 tw-py-2.5 tw-text-xs tw-font-bold tw-text-[#F1416C] hover:tw-bg-[#c20326] hover:tw-text-white',
                    FOCUS_VISIBLE_RING_CLASS,
                  )}
                  onClick={onCancel}
                  type="button"
                >
                  {dialog.cancelText}
                </button>
              </div>
            </div>
          </div>
        </div>
      </ConfirmationPortal>
    );
  }

  return (
    <ConfirmationPortal>
      <div
        className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-[rgba(17,24,39,0.58)] tw-p-4 sm:tw-p-6"
        onClick={() => {
          if (dialog.closeOnBackdropClick) {
            onCancel();
          }
        }}
      >
        <div
          aria-busy={dialog.isLoading}
          aria-describedby={messageId}
          aria-labelledby={titleId}
          aria-modal="true"
          className="tw-relative tw-w-[476px] tw-max-w-full !tw-h-[305px] tw-overflow-hidden tw-rounded-lg tw-border tw-border-[rgba(226,232,240,0.9)] tw-bg-white tw-p-5 tw-shadow-[0_24px_80px_rgba(15,23,42,0.22)] max-sm:tw-min-h-0"
          onClick={(event) => event.stopPropagation()}
          role="alertdialog"
        >
          <div className="tw-flex tw-flex-col tw-h-full">
            <div className="tw-mb-6 tw-flex tw-justify-end">
              <button
                aria-label={intl.formatMessage({ id: 'close' })}
                className={`tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-md tw-bg-transparent tw-p-0 tw-text-[#626779] tw-transition-colors tw-duration-150 hover:tw-text-slate-700 ${FOCUS_VISIBLE_RING_CLASS}`}
                onClick={onCancel}
                type="button"
              >
                <SVGSpriteIcon
                  icon="NewCloseIcon"
                  className="tw-w-3 tw-h-3 tw-opacity-50 hover:tw-opacity-100"
                />
              </button>
            </div>
            <div className="tw-flex-1 tw-mb-3">
              <h2 className="tw-mb-3 tw-text-center tw-text-xl tw-font-bold" id={titleId}>
                {dialog.title || dialog.message}
              </h2>
              {dialog.title && dialog.message && (
                <p className="tw-text-center tw-mb-3 tw-text-sm">{dialog.message}</p>
              )}
            </div>
            <div>
              <button
                className={clsx(
                  'tw-mb-3 tw-w-full tw-rounded-md tw-border tw-px-5 tw-py-2.5 tw-text-xs tw-font-bold tw-text-white',
                  BUTTON_VARIANT_STYLES[dialog.variant],
                  FOCUS_VISIBLE_RING_CLASS,
                )}
                onClick={onConfirm}
                type="button"
              >
                {dialog.isLoading ? intl.formatMessage({ id: 'loading' }) : dialog.confirmText}
              </button>
              <button
                className={`tw-w-full tw-rounded-md tw-border tw-border-primary tw-px-5 tw-py-2.5 tw-text-xs tw-font-bold tw-text-[#0368ff] ${FOCUS_VISIBLE_RING_CLASS}`}
                onClick={onCancel}
                type="button"
              >
                {dialog.cancelText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ConfirmationPortal>
  );
};

export default Confirmation;
